import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { useTranslation } from "react-i18next";

import { useDeleteZoneZonesIdDeleteMutation } from "api";
import { ZonePermissionEnum } from "authorization/types";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useNavigate } from "hooks/useNavigate";
import { IZone } from "interfaces/zone.interface";

type DeleteZoneDialogProps = {
  zone: Pick<IZone, "id" | "parent_id">;
};

export function DeleteZoneDialog({ zone }: DeleteZoneDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { permissions } = useAuthenticatedUser();

  const [deleteError, setDeleteError] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [deleteZone] = useDeleteZoneZonesIdDeleteMutation();

  const onDelete = async () => {
    try {
      await deleteZone({ id: zone.id });

      setShowDelete(false);

      navigate(`/administration/zones/${zone.parent_id ?? ""}`);
    } catch {
      setDeleteError(true);
    }
  };

  return (
    <>
      <Dialog open={deleteError} onClose={() => setDeleteError(false)}>
        <DialogTitle>{t("information")}</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {t("cannot_delete_the_selected_zone_as_it_is_not_empty")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteError(false)}>{t("ok")}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={showDelete} onClose={() => setShowDelete(false)}>
        <DialogTitle>{t("Delete zone")}</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {t("Are you sure you want to delete this zone?")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color={"error"} onClick={onDelete}>
            {t("Delete")}
          </Button>
          <Button
            color={"primary"}
            variant={"text"}
            onClick={() => setShowDelete(false)}
          >
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      <Button
        color={"error"}
        disabled={!permissions.includes(ZonePermissionEnum["zone.delete"])}
        onClick={() => setShowDelete(true)}
      >
        {t("Delete")}
      </Button>
    </>
  );
}
